import clsx from "clsx";
import { useEffect, useMemo, useState } from "react";
import { useForm } from "react-hook-form";
import { useAlert } from "../../shared/components/AlertProvider";
import CheckBox from "../../shared/components/CheckBox";
import Flex from "../../shared/components/Flex";
import Grid from "../../shared/components/Grid";
import GridChild from "../../shared/components/GridChild";
import Label from "../../shared/components/Label";
import LabelInput from "../../shared/components/LabelInput";
import ModalButton from "../../shared/components/ModalButton";
import Spacer from "../../shared/components/Spacer";
import Text from "../../shared/components/Text";
import AllianceStyle from "../system/alliance/AllianceStyle.module.css";
import MyProfileStyle from "./css/MyProfile.module.css";
import { responseMsg } from "./ResponseMsg";

type MyProfileProps = {
    closeMyModal: () => void;
};

type MyProfileForm = {
    userId: string;
    allianceName: string;
    department: string;
    userNm: string;
    cellNo: string;
    password: string;
    email: string;
    encodedNm: string;
};

function MyProfile({ closeMyModal }: MyProfileProps) {
    const [passwordLocked, setPasswordLocked] = useState(true);
    const [passwordValue, setPasswordValue] = useState("");
    const [regions, setRegions] = useState<any[]>([]);
    const [selectedRegion1, setSelectedRegion1] = useState<any | null>(null);
    const [checkedIds, setCheckedIds] = useState<number[]>([]);
    const openAlert = useAlert();
    const userId = typeof window !== "undefined" ? localStorage.getItem("USER") : "";

    const { register, handleSubmit, reset } = useForm<MyProfileForm>({
        defaultValues: useMemo(
            () => ({
                userId: "",
                allianceName: "",
                department: "",
                userNm: "",
                cellNo: "",
                password: "",
                email: "",
                encodedNm: "",
            }),
            []
        ),
    });

    const autoHyphen = (target: HTMLInputElement) => {
        // 전화번호 하이픈 자동 추가
        target.value = target.value
            .replace(/[^0-9]/g, "")
            .replace(/^(\d{0,3})(\d{0,4})(\d{0,4})$/g, "$1-$2-$3")
            .replace(/(\-{1,2})$/g, "");
    };

    const onSubmit = (formData: MyProfileForm) => {
        const payload = {
            department: formData.department,
            userNm: formData.userNm,
            cellNo: formData.cellNo,
            password: passwordValue || null,
            email: formData.email,
        };

        fetch(`/api/system/users/${userId}`, {
            method: "PATCH",
            headers: { "Content-Type": "application/json" },
            body: JSON.stringify(payload),
        })
            .then((res) => res.json())
            .then((json) => {
                if (json.code === responseMsg.SUCCESS.code) {
                    openAlert({
                        title: "알림",
                        text: "정상적으로 처리되었습니다.",
                    });
                    closeMyModal();
                } else {
                    openAlert({
                        title: "오류",
                        text: "처리 중 문제가 발생했습니다.",
                        type: "error",
                    });
                }
            })
            .catch(() => {
                openAlert({
                    title: "오류",
                    text: "처리 중 문제가 발생했습니다.",
                    type: "error",
                });
            });
    };

    useEffect(() => {
        if (!userId) return;

        fetch(`/api/system/users/${userId}`, { method: "GET" })
            .then((res) => res.json())
            .then((json) => {
                if (json.code === responseMsg.SUCCESS.code) {
                    const data = json.data || {};
                    const allianceRegions = data.alliance?.regions || [];
                    setRegions(allianceRegions);
                    const firstDepth2 = allianceRegions.find((r: any) => r.depth === 2) || null;
                    setSelectedRegion1(firstDepth2);
                    setCheckedIds(allianceRegions.map((r: any) => r.id));
                    reset({
                        userId: data.userId ?? "",
                        allianceName: data.alliance?.allianceName ?? "",
                        department: data.department ?? "",
                        userNm: data.userNm ?? "",
                        cellNo: data.cellNo ?? "",
                        password: "",
                        email: data.email ?? "",
                        encodedNm: data.role.encodedNm ?? "",
                    });
                } else {
                    openAlert({
                        title: "오류",
                        text: "처리 중 문제가 발생했습니다.",
                        type: "error",
                    });
                }
            })
            .catch(() => {
                openAlert({
                    title: "오류",
                    text: "처리 중 문제가 발생했습니다.",
                    type: "error",
                });
            });
    }, [reset, openAlert, userId]);

    return (
        <div id="myProfile">
            <form onSubmit={handleSubmit(onSubmit)}>
                <Flex flexDirection="column" gap={16}>
                    <Flex gap={12} flexWrap="wrap">
                        <LabelInput
                            id="userId"
                            label={<Text size={14} weight="medium">아이디</Text>}
                            readOnly
                            fullWidth
                            {...register("userId")}
                        />
                        <LabelInput
                            id="allianceName"
                            label={<Text size={14} weight="medium">소속</Text>}
                            readOnly
                            fullWidth
                            {...register("allianceName")}
                        />
                    </Flex>

                    <Flex gap={12} flexWrap="wrap">
                        <LabelInput
                            id="department"
                            label={<Text size={14} weight="medium">부서</Text>}
                            placeholder="부서를 입력하세요"
                            fullWidth
                            {...register("department")}
                        />
                        <LabelInput
                            id="userNm"
                            label={<Text size={14} weight="medium">이름</Text>}
                            placeholder="이름을 입력하세요"
                            fullWidth
                            {...register("userNm")}
                        />
                    </Flex>

                    <Flex gap={12} flexWrap="wrap">
                        <LabelInput
                            id="cellNo"
                            label={<Text size={14} weight="medium">연락처</Text>}
                            placeholder="숫자만 입력하세요"
                            maxLength={13}
                            fullWidth
                            {...register("cellNo", {
                                onChange: ({ target }) => autoHyphen(target as HTMLInputElement),
                            })}
                        />
                        <LabelInput
                            id="email"
                            label={<Text size={14} weight="medium">이메일</Text>}
                            placeholder="이메일을 입력하세요"
                            type="email"
                            fullWidth
                            {...register("email")}
                        />
                    </Flex>

                    <Flex gap={12} flexWrap="wrap" alignItems="flex-end">
                        <LabelInput
                            id="password"
                            label={<Text size={14} weight="medium">비밀번호</Text>}
                            placeholder={
                                passwordLocked
                                    ? "잠금 해제 시 변경 가능합니다."
                                    : "비밀번호를 입력해 주세요."
                            }
                            type={passwordLocked ? "password" : "text"}
                            readOnly={passwordLocked}
                            value={passwordValue}
                            onChange={(e) => setPasswordValue(e.target.value)}
                            fullWidth
                            rightIcon={
                                <div
                                    onClick={(e) => {
                                        e.preventDefault();
                                        e.stopPropagation();
                                        setPasswordLocked((prev) => !prev);
                                        setPasswordValue("");
                                    }}
                                    className={clsx(
                                        MyProfileStyle["password-icon-wrapper"],
                                        passwordLocked ? MyProfileStyle.locked : MyProfileStyle.unlocked
                                    )}
                                    style={{
                                        cursor: "pointer",
                                        color: passwordLocked ? "#B0B8C1" : "#1B84FF",
                                    }}
                                >
                                    {passwordLocked ? <img alt="" src="/images/ico_lock.png" /> : <img alt="" src="/images/ico_unlock.png" />}
                                </div>
                            }
                        />
                        <LabelInput
                            id="encodedNm"
                            label={<Text size={14} weight="medium">권한</Text>}
                            readOnly
                            fullWidth
                            {...register("encodedNm")}
                        />
                    </Flex>

                    {/* 지역 권한 (AllianceEditModal 동일 UI) */}
                    <Grid paddingX={0} gap={16}>
                        <GridChild span={2}>
                            <Flex flexDirection="column" gap={8}>
                                <Label>시/도 권한</Label>
                                <Grid paddingX={0} gap={5} style={{
                                    height: "150px", overflow: "auto",
                                    borderTop: "1px solid #E4E4E4",
                                    borderBottom: "1px solid #E4E4E4",
                                    paddingTop: "15px",
                                    paddingBottom: "15px",
                                    }}>
                                    {regions
                                        .filter((ele) => ele.depth === 2)
                                        .map((ele) => {
                                            const children = regions.filter((r) => r.parent === ele.id && r.depth === 3);
                                            const isSelected = selectedRegion1?.id === ele.id;
                                            return (
                                                <div
                                                    key={ele.id}
                                                    className={`${AllianceStyle.checkBoxWrap} ${isSelected ? AllianceStyle.selected : ""}`}
                                                    onClick={() => setSelectedRegion1(ele)}
                                                    style={{
                                                        cursor: "pointer",
                                                        fontSize: "17px",
                                                        fontWeight: "600",
                                                        border: "1px solid transparent",
                                                        borderColor: isSelected ? "#FFE8A9" : "transparent",
                                                        borderRadius: 8,
                                                        backgroundColor: isSelected ? "#FFFCF4" : "transparent",
                                                        boxSizing: "border-box",
                                                    }}
                                                >
                                                    <label htmlFor={ele.id} style={{ cursor: "pointer" }}>
                                                        <Text size={14} weight={isSelected ? "semibold" : "medium"}>
                                                            {`${ele.name}(${children.length})`}
                                                        </Text>
                                                    </label>
                                                </div>
                                            );
                                        })}
                                </Grid>
                            </Flex>
                        </GridChild>
                        <GridChild span={2}>
                            <Flex flexDirection="column" gap={8}>
                                <Label>군/구 권한</Label>
                                <Grid
                                    paddingX={0}
                                    gap={12}
                                    templateColumn="1fr 1fr 1fr"
                                    style={{
                                        height: "84px",
                                        overflow: "auto",
                                        borderTop: "1px solid #E4E4E4",
                                        borderBottom: "1px solid #E4E4E4",
                                        paddingTop: "15px",
                                        paddingBottom: "15px",
                                    }}
                                >
                                    {selectedRegion1 && (
                                        <>
                                            {regions
                                                .filter((ele) => ele.depth === 3 && ele.parent === selectedRegion1.id)
                                                .map((ele) => (
                                                    <Flex key={ele.id} alignItems="center" gap={10} style={{ fontSize: "17px", fontWeight: "500" }}>
                                                        <CheckBox checked={checkedIds.includes(ele.id)} disabled />
                                                        <label htmlFor={ele.id}>
                                                            <Text size={14} weight={"medium"}>
                                                                {ele.name}
                                                            </Text>
                                                        </label>
                                                    </Flex>
                                                ))}
                                        </>
                                    )}
                                </Grid>
                            </Flex>
                        </GridChild>
                    </Grid>

                </Flex>

                <Spacer direction="vertical" size={24} />

                <Flex justifyContent="flex-end" gap={8} flexWrap="wrap">
                    <ModalButton
                        type="update"
                        variant="primary"
                        text="변경"
                        onClick={handleSubmit(onSubmit)}
                    />
                    <ModalButton
                        type="cancel"
                        variant="white"
                        text="취소"
                        onClick={closeMyModal}
                    />
                </Flex>
            </form>
        </div>
    );
}

export default MyProfile;
